/**
 * Thème clair et thème sombre.
 *
 * L'application était sombre, et le reste **par défaut**. Le plan demandait
 * « un thème clair en plus du sombre » : ajouter un choix, pas basculer tout
 * le monde. Suivre le réglage du système d'emblée aurait changé l'apparence de
 * chaque utilisateur sur machine claire sans que personne ne l'ait demandé —
 * c'est le même raisonnement que sur les seuils de relance.
 *
 * Le choix est **par navigateur**, pas par compte : c'est une préférence de
 * confort visuel, qui dépend de l'écran et de la lumière de la pièce. Le même
 * patron peut vouloir le clair sur le portable du bureau et le sombre sur son
 * téléphone le soir. Le stocker en base l'imposerait aux deux.
 */

/** Ce que l'utilisateur choisit. */
export type ThemePreference = "sombre" | "clair" | "systeme";

/** Ce qui est réellement appliqué au document. */
export type ResolvedTheme = "sombre" | "clair";

export const THEME_STORAGE_KEY = "dker.theme";
export const DEFAULT_THEME_PREFERENCE: ThemePreference = "sombre";

const PREFERENCES: readonly ThemePreference[] = ["sombre", "clair", "systeme"];

/** Couleur de la barre d'adresse mobile, par thème résolu. */
const THEME_COLOR: Record<ResolvedTheme, string> = {
  sombre: "#16181C",
  clair: "#F5F5F3",
};

export function isThemePreference(v: unknown): v is ThemePreference {
  return typeof v === "string" && (PREFERENCES as readonly string[]).includes(v);
}

/**
 * Le thème effectif.
 *
 * Pur, donc testable sans navigateur : c'est la seule règle de ce fichier qui
 * mérite d'être vérifiée, le reste n'étant que de l'écriture dans le DOM.
 */
export function resolveTheme(
  preference: ThemePreference,
  systemPrefersLight: boolean,
): ResolvedTheme {
  if (preference === "clair") return "clair";
  if (preference === "sombre") return "sombre";
  return systemPrefersLight ? "clair" : "sombre";
}

export function systemPrefersLight(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: light)").matches
  );
}

export function readThemePreference(): ThemePreference {
  try {
    const brut = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(brut) ? brut : DEFAULT_THEME_PREFERENCE;
  } catch {
    // Navigation privée, stockage refusé par le navigateur, quota plein : le
    // thème est un confort, il ne doit jamais empêcher l'application de
    // démarrer.
    return DEFAULT_THEME_PREFERENCE;
  }
}

export function writeThemePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Idem : le choix vaudra pour la session, pas au-delà.
  }
}

/**
 * Pose le thème sur le document.
 *
 * `data-theme` porte la palette CSS, et `color-scheme` fait suivre ce que le
 * navigateur dessine lui-même — ascenseurs, sélecteur de couleur natif du
 * formulaire d'identité visuelle, champs de date. Sans lui, ces éléments
 * restent sombres au milieu d'une interface claire.
 *
 * Les valeurs de l'attribut sont en anglais alors que tout le reste du dépôt
 * est en français : elles côtoient `color-scheme: light` et
 * `prefers-color-scheme`, dont le vocabulaire est imposé par CSS. Mélanger
 * « clair » et `light` dans la même règle coûterait plus en confusion que la
 * cohérence n'y gagnerait.
 */
export function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.dataset.theme = resolved === "clair" ? "light" : "dark";
  root.style.colorScheme = resolved === "clair" ? "light" : "dark";

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR[resolved]);
}
